import { useFormikContext } from 'formik'
import React, { useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { FormGroup } from 'components'
import Icon from 'components/Icon'
import InfoTooltip from 'components/InfoTooltip'
import { _, colors } from 'utils'
import { useDeleteItem } from 'hooks'
import { addModal } from 'features/modals/modalSlice'
import { InviteEmailsProps } from '../../types'
import InviteEmailForm from './InviteEmailForm'

const S = {
    FormGroup: styled(FormGroup)`
        display: flex;
        flex-direction: column;
    `,
    InputWrapper: styled.div`
        display: flex;
        align-items: center;
        width: 100%;
        min-height: 44px;
        height: 100%;
        border: 1px solid ${colors.inputBorder};
        font-size: 14px;
        color: ${colors.grayTitle};
        border-radius: 4px;
    `,
    Emails: styled.div`
        position: relative;
        min-height: 44px;
        display: flex;
        flex: 1;
        flex-wrap: wrap;
        align-items: center;
        padding: 7px 8px 0 8px;
    `,
    EmailItem: styled.div<{ isActive?: boolean }>`
        display: flex;
        align-items: center;
        max-width: 250px;
        height: 30px;
        margin: 0 8px 7px 0;
        padding: 0 8px;
        background-color: ${({ isActive }) => (isActive ? colors.grayTitle : colors.iconOpacity(0.05))};
        border: 1px ${({ isActive }) => (isActive ? `solid ${colors.grayTitle}` : `dashed ${colors.iconOpacity(0.25)}`)};
        color: ${({ isActive }) => (isActive ? 'white' : colors.grayTitle)};
        border-radius: 5px;
    `,
    CloseIcon: styled(Icon)`
        margin: 0 0 2px 8px;
    `,
    AddButton: styled.div<{ disabled?: boolean }>`
        display: flex;
        align-items: center;
        height: 100%;
        max-width: 100px;
        padding: 0 16px;
        overflow: hidden;
        font-size: 14px;
        color: ${colors.iconOpacity(0.9)};
        border-left: 1px solid ${colors.inputBorder};
        cursor: pointer;
        user-select: none;
        opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
        pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
    `,
    InfoTooltip: styled(InfoTooltip)`
        position: absolute;
        top: 10px;
        bottom: 10px;
        right: 10px;
        display: flex;
        align-items: center;
        & > span {
            margin: 0;
        }
    `
}

const InviteEmailsField = ({
    name,
    label,
    isRequired,
    onInvite = () => Promise.resolve(null),
    onRemove = () => Promise.resolve(null),
    helperText,
    modalSize = 'lg',
    callback = () => null,
    staffsInfo,
    ...props
}: InviteEmailsProps) => {
    const dispatch = useDispatch()
    const { values } = useFormikContext()
    const [renderEmails, setRenderEmails] = useState(values[name] || [])
    const [total, setTotal] = useState(staffsInfo.total)

    useEffect(() => {
        if (values[name]) {
            setRenderEmails(values[name])
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values[name]])

    const deleteFn = async (email) => {
        const res = await onRemove(email)
        setRenderEmails((emails) => emails.filter((item) => item.email !== email))
        setTotal(total - 1)
        return res
    }
    const { showDeleteConfirmModal } = useDeleteItem(
        deleteFn, callback
    )

    const handleSubmit = (email) => {
        setTotal(total + 1)
        return onInvite(email)
    }

    const showAddModal = () => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <InviteEmailForm
                    close={close}
                    name={name}
                    onSubmit={handleSubmit}
                    callback={callback}
                    setNewAddedItems={setRenderEmails}
                />
            ),
            size: modalSize,
            header: _('Invite')
        }))
    }

    return (
        <S.FormGroup
            controlId={`${name}-invite-emails-field`}
            {...props}
        >
            <Form.Label>
                {label}
                {isRequired && <span>&nbsp;*</span>}
            </Form.Label>
            <S.InputWrapper>
                <S.Emails>
                    {renderEmails.map((item) => (
                        <S.EmailItem key={item.id} isActive={item.hasLoggedOn}>
                            <span className="text-cut">{item.email}</span>
                            <S.CloseIcon
                                icon="close"
                                size={18}
                                color={item.hasLoggedOn ? 'white' : colors.icon}
                                onClick={() => showDeleteConfirmModal(item.email)}
                            />
                        </S.EmailItem>
                    ))}
                    <S.InfoTooltip size={24} text={helperText} />
                </S.Emails>
                <S.AddButton onClick={showAddModal} disabled={total >= staffsInfo.max}>
                    {_('button.add')}
                </S.AddButton>
            </S.InputWrapper>
        </S.FormGroup>
    )
}

export default InviteEmailsField
